// Promise-gated single-flight confirm controller (T6).
//
// Every inventory commit path (cell popover, Cmd+K palette) calls the same
// gated handler and awaits a promise. `request(payload)` stashes the payload +
// the promise's resolver and surfaces it as `pending` so a modal can render.
// The user's choice drives the outcome:
//   - runConfirm(executor): await the real API call, then resolve/reject the
//     original promise. The gate stays "busy" for the whole await so a second
//     entry path can't slip in.
//   - cancel(): reject the original promise. Callers (popover/palette) catch a
//     rejected promise as a failure and stay open so the user can fix the qty.
//
// Single-flight: while one request is pending, a second `request(...)` is
// rejected immediately rather than overwriting the live resolver (a double-
// Enter or a second path can't clobber the first).

import { useCallback, useRef, useState } from 'react';

export function useConfirmGate() {
  const [pending, setPending] = useState(null);   // { payload, inFlight } | null
  const ref = useRef(null);                        // { payload, resolve, reject, running }

  const request = useCallback((payload) => {
    if (ref.current) return Promise.reject(new Error('已有待确认的操作'));
    return new Promise((resolve, reject) => {
      ref.current = { payload, resolve, reject, running: false };
      setPending({ payload, inFlight: false });
    });
  }, []);

  const runConfirm = useCallback(async (executor) => {
    const entry = ref.current;
    if (!entry || entry.running) return;          // ignore double-confirm
    entry.running = true;
    setPending(p => (p ? { ...p, inFlight: true } : p));
    try {
      const result = await executor(entry.payload);
      ref.current = null;
      setPending(null);
      entry.resolve(result);
    } catch (e) {
      ref.current = null;
      setPending(null);
      entry.reject(e);
    }
  }, []);

  const cancel = useCallback(() => {
    const entry = ref.current;
    if (!entry || entry.running) return;          // can't cancel mid-flight
    ref.current = null;
    setPending(null);
    entry.reject(new Error('已取消'));
  }, []);

  return { pending, request, runConfirm, cancel };
}
